import React from 'react';

import { Group, Stack, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';

import { MeasurementSystem, MeasurementSystemOptions, Project, WireSizingStandardOptions } from 'models';

import { VoltageDropField } from 'components/component-fields/VoltageDropField';
import { Form } from 'components/forms/Form';
import { FormSubmit } from 'components/forms/FormSubmit';
import { SelectField, SelectFieldProps } from 'components/forms/fields/SelectField';
import { useCurrentProject } from 'hooks/use-current-project';
import { useMeasurementSystem } from 'hooks/use-measurement-system';
import { MeasurementSystemService } from 'services/MeasurementSystemService';
import { ProjectService } from 'services/ProjectService';

type FieldValues = {
    standard: Project['wireSizing']['standard'];
    voltageDrop: Project['wireSizing']['voltageDrop'];
    measurementSystem?: MeasurementSystem;
};

const WireSizeSettingsModal = ({ context }: ContextModalProps) => {
    const project = useCurrentProject();
    const measurementSystem = useMeasurementSystem();

    if (!project) {
        return null;
    }

    const defaultValues: FieldValues = {
        standard: project.wireSizing.standard,
        voltageDrop: project.wireSizing.voltageDrop,
        measurementSystem,
    };

    const save = async (values: FieldValues) => {
        const { standard, voltageDrop, measurementSystem: units } = values;

        await ProjectService.update(project.id, {
            wireSizing: {
                ...project.wireSizing,
                standard,
                voltageDrop,
            },
        });

        if (units) {
            MeasurementSystemService.set(units);
        }

        context.closeAll();
    };

    return (
        <Form<FieldValues> defaultValues={defaultValues} onSubmit={save}>
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Text size="sm" fw={500}>
                        Standard
                    </Text>
                    <StandardField />
                </Group>

                <Group justify="space-between" align="center">
                    <Text size="sm" fw={500}>
                        Measurement System
                    </Text>
                    <MeasurementSystemField />
                </Group>

                <Group justify="space-between" align="center">
                    <Text size="sm" fw={500}>
                        Default Voltage Drop
                    </Text>
                    <VoltageDropField name="voltageDrop" w={120} />
                </Group>

                <Text size="xs" c="dimmed">
                    New connections will use this voltage drop by default. When a connection has no specific voltage
                    drop defined we will fallback to this value.
                </Text>

                <FormSubmit disableIfClean>Save settings</FormSubmit>
            </Stack>
        </Form>
    );
};

const StandardField = (props: Partial<SelectFieldProps>) => {
    return <SelectField inline {...props} name="standard" data={WireSizingStandardOptions} />;
};

const MeasurementSystemField = (props: Partial<SelectFieldProps>) => {
    return <SelectField inline {...props} name="measurementSystem" data={MeasurementSystemOptions} />;
};

export { WireSizeSettingsModal };
